import React from 'react';
import { useImpressionItem } from '@fiverr-private/impressionable';
import { Section, Stack } from '@fiverr-private/layout_components';
import { I18n } from '@fiverr-private/i18n-react';
import { Text, Typography } from '@fiverr-private/typography';
import { Box } from '@fiverr-private/theme';
import { Button } from '@fiverr-private/button';
import { Video } from '@fiverr-private/media';
import { getContext } from '@fiverr-private/fiverr_context';
import { Domingo } from '@fiverr-private/domingo';
import { getImpressionEnrichment, sendBigQueryEvent } from '../../utils/biEvents';
import { BQ_HOMEPAGE_CLICK, BQ_HOMEPAGE_IMP } from '../../utils/biEvents/events/bigQueryEvents';
import SafeComponent from '../../shared/SafeComponent';
import { AI_BANNER_POSTER_URL, AI_BANNER_VIDEO_URL, AI_DIRECTOR_LINK_PATH, AI_DIRECTOR_UTM_SOURCE } from './constants';
import { bannerClass, ctaButtonClass, descriptionClass, mediaWrapperClass, titleClass } from './styles.ve.css';

const getAiBannerLink = (): string => {
    const { url } = getContext();

    return `${Domingo(url).removePath().url}${AI_DIRECTOR_LINK_PATH}?utm_source=${AI_DIRECTOR_UTM_SOURCE}`;
};

const AiBannerSection = () => {
    const [ref] = useImpressionItem(getImpressionEnrichment({ eventName: BQ_HOMEPAGE_IMP }));
    const ctaLink = getAiBannerLink();

    const onButtonClick = () => {
        sendBigQueryEvent({
            eventName: BQ_HOMEPAGE_CLICK,
            params: { elementName: 'ai_video_banner', elementType: 'button' },
        });
    };

    return (
        <Section paddingTop="0" paddingBottom={{ default: '10', sm: '12', md: '12', lg: '10' }}>
            <Stack
                ref={ref}
                className={bannerClass}
                direction={{ default: 'column', md: 'row' }}
                alignItems="center"
                justifyContent="spaceBetween"
                width="100%"
                height="auto"
                gap="6"
                background="#000"
                borderRadius="2xl"
                overflow="hidden"
            >
                <Stack
                    direction="column"
                    color="white"
                    gap="0"
                    width="100%"
                    maxWidth={{ default: '100%', md: '285px', lg: '515px' }}
                    flexShrink={0}
                    order={{ default: 2, md: 1 }}
                    paddingLeft={{ default: '5', md: '0' }}
                    paddingRight={{ default: '5', md: '0' }}
                >
                    <Stack direction="column" gap="4" marginBottom="6">
                        <Typography as="h2" color="inherit" className={titleClass}>
                            <I18n k="logged_out_homepage.ai_banner_section.title" />
                        </Typography>
                        <Text color="inherit" className={descriptionClass}>
                            <I18n k="logged_out_homepage.ai_banner_section.description" />
                        </Text>
                    </Stack>
                    <Stack direction="column" gap="4" width={{ default: '100%', sm: 'fit-content' }}>
                        <Button
                            href={ctaLink}
                            size="lg"
                            fullWidth
                            variant="filled"
                            colorScheme="white"
                            onClick={onButtonClick}
                            className={ctaButtonClass}
                        >
                            <I18n k="logged_out_homepage.ai_banner_section.cta" />
                        </Button>
                    </Stack>
                </Stack>
                <Box
                    className={mediaWrapperClass}
                    order={{ default: 1, md: 2 }}
                    position="relative"
                    overflow="hidden"
                    width="100%"
                    flex={1}
                    minWidth={0}
                    background="#000"
                    minHeight={{ default: '225px', sm: '300px', md: '416px' }}
                >
                    <Video
                        width="100%"
                        objectFit="contain"
                        autoPlay
                        playsInline
                        controls={false}
                        loop
                        muted
                        preload="auto"
                        poster={AI_BANNER_POSTER_URL}
                        aria-hidden
                        sources={[
                            {
                                src: AI_BANNER_VIDEO_URL,
                                type: 'video/mp4',
                            },
                        ]}
                    />
                </Box>
            </Stack>
        </Section>
    );
};

export { AiBannerSection };
export default SafeComponent(AiBannerSection);
